import * as React from "react";
import cn from "classnames";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ColumnDef, Row } from "@tanstack/react-table";
import { format } from "date-fns";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Card,
    Checkbox,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FormCheckbox,
    Pagination,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
    SelectElement,
    Table,
} from "@jhool-io/fe-components";
import { IStatementOfAccount } from "../../types/reports.types";
import {
    convertDateFilterStringToDate,
    displayNameInRightFormat,
    getTotalAmountOfTableRowsSelected,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
    truncateString,
} from "../../../../utils/helpers";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { IAppCustomDates } from "../../../../utils/types";
import { useDebounce } from "../../../../hooks/helpers";
import {
    useFetchStatementOfAccount,
    useGetDownloadJobId,
} from "../../hooks/queries/reports.queries";
import { useFetchClientList } from "../../../../hooks/queries/client";
import useToast from "../../../../hooks/useToast";
import { useExportPdfs } from "../../../../hooks/queries";
import ChevronDownIcon from "../../../../components/Icons/ChevronDown";
import { APP_COLORS, LISTS_DEFAULT_LIMIT } from "../../../../utils/constants";
import CheckPrimaryColorIcon from "../../../../components/Icons/CheckPrimaryColor";
import ListState from "../../../../components/ListState/ListState";
import { useSendStatementOfAccount } from "../../hooks/mutations/reports.mutations";
import { useAppSelector } from "../../../../hooks/useRedux";
import CalendarIcon from "../../../../icons/Calendar";
import ExportLoaderAndText from "../../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import Loader from "../../../../shared-ui/Loader/Loader";
import InsuranceProviderFilter, {
    getInsuranceProviderIdsFilter,
    setInsuranceProviderIdsFilter,
} from "../../../../shared-ui/InsuranceProviderFilter/InsuranceProviderFilter";

type Options = {
    label: React.ReactNode;
    value: string;
};

const amountDue = ["0", "20000", "40000", "60000", "80000", "100000"];

const columns: ColumnDef<IStatementOfAccount>[] = [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                handleChange={table.getToggleAllRowsSelectedHandler()}
                indeterminate={table.getIsSomeRowsSelected()}
                label=""
                name="row"
                value="row"
                isChecked={table.getIsAllRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                handleChange={row.getToggleSelectedHandler()}
                indeterminate={row.getIsSomeSelected()}
                label=""
                name="row"
                value="row"
                isChecked={row.getIsSelected()}
            />
        ),
    },
    {
        accessorKey: "client name",
        header: "Client name",
        cell: ({ row }) =>
            displayNameInRightFormat(
                row.original.statement_summary.client_name
            ),
    },
    {
        accessorKey: "Total Amount Outstanding",
        header: "Total Amount Due",
        cell: ({ row }) =>
            row.original.statement_summary.total_amount_outstanding
                ? showMoneyInAppFormat(
                      row.original.statement_summary.total_amount_outstanding
                  )
                : "--",
    },
    {
        accessorKey: "Amount Outstanding Insurance",
        header: "Insurance Due",
        cell: ({ row }) =>
            row.original.statement_summary.amount_outstanding_ins
                ? showMoneyInAppFormat(
                      row.original.statement_summary.amount_outstanding_ins
                  )
                : "--",
    },
    {
        accessorKey: "Amount Outstanding Co-Insurance",
        header: "Co-Insurance Due",
        cell: ({ row }) =>
            row.original.statement_summary.amount_outstanding_coins
                ? showMoneyInAppFormat(
                      row.original.statement_summary.amount_outstanding_coins
                  )
                : "--",
    },
    {
        accessorKey: "Insurance Provider",
        header: "Insurance Provider",
        cell: ({ row }) => (
            <span className="uppercase">
                {row.original.statement_summary.insurance_provider || "--"}
            </span>
        ),
    },
];

export default function StatementOfAccountPreview() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchValue, setSearchValue] = React.useState("");
    const [showDefault, setShowDefault] = React.useState(false);
    const [showOptions, setShowOptions] = React.useState(false);
    const [providerSearchValue, setProviderSearchValue] = React.useState("");
    const [showDefaultProviders, setShowDefaultProviders] =
        React.useState(false);
    const [showProviderOptions, setShowProviderOptions] = React.useState(false);
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [showLoader, setShowLoader] = React.useState(false);
    const [allTimeCoInsurance, setAllTimeCoInsurance] = React.useState(false);
    const [allTimeInsurance, setAllTimeInsurance] = React.useState(false);
    const [showEmailDialog, setShowEmailDialog] = React.useState(false);

    const [rowSelection, setRowSelection] = React.useState({});
    const [getSelectedData, setGetSelectedData] =
        React.useState<Row<IStatementOfAccount>[]>();

    const navigate = useNavigate();

    const selectRef = React.useRef<SelectElement | null>(null);
    const providerSelectRef = React.useRef<SelectElement | null>(null);

    const DurationTypesForSelect: Options[] = [
        ...Object.values(IAppCustomDates).map((item) => ({
            value: item,
            label: (
                <>{makeStringFirstLetterCapital(removeEnumUnderscore(item))} </>
            ),
        })),
    ];

    const clientsFilter =
        searchParams.getAll("clients[]").length > 0
            ? searchParams.getAll("clients[]")
            : [];
    const providersFilter =
        searchParams.getAll("providers[]").length > 0
            ? searchParams.getAll("providers[]")
            : [];
    const insuranceProviderIdsFilter =
        getInsuranceProviderIdsFilter(searchParams);
    const coInsuranceFromFilter =
        searchParams.get("coInsurance_from") || undefined;
    const coInsuranceToFilter = searchParams.get("coInsurance_to") || undefined;
    const insuranceFromFilter = searchParams.get("insurance_from") || undefined;
    const insuranceToFilter = searchParams.get("insurance_to") || undefined;
    const customDateFilter =
        (searchParams.get("custom_date") as IAppCustomDates) || undefined;
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const pageFilter = Number(searchParams.get("page")) || 1;
    const limitFilter =
        Number(searchParams.get("limit")) || LISTS_DEFAULT_LIMIT;

    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);

    const { practice } = useAppSelector((state) => state.userPractice);

    const debouncedValue = useDebounce(searchValue, 500);
    const debouncedProviderSearch = useDebounce(providerSearchValue, 500);

    const shouldFetchStatementPreview =
        clientsFilter.length > 0 ||
        providersFilter.length > 0 ||
        insuranceProviderIdsFilter.length > 0 ||
        Boolean(customDateFilter) ||
        Boolean(fromDateFilter) ||
        Boolean(toDateFilter) ||
        Boolean(coInsuranceFromFilter) ||
        Boolean(coInsuranceToFilter) ||
        Boolean(insuranceFromFilter) ||
        Boolean(insuranceToFilter) ||
        Boolean(allTimeCoInsurance) ||
        Boolean(allTimeInsurance);

    const statementPreview = useFetchStatementOfAccount(
        {
            action: "preview",
            client_ids: clientsFilter,
            provider_ids: providersFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            custom_date: customDateFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            from_amount_outstanding_coins: allTimeCoInsurance
                ? "0"
                : coInsuranceFromFilter,
            to_amount_outstanding_coins: allTimeCoInsurance
                ? "100000"
                : coInsuranceToFilter,
            from_amount_outstanding_ins: allTimeInsurance
                ? "0"
                : insuranceFromFilter,
            to_amount_outstanding_ins: allTimeInsurance
                ? "100000"
                : insuranceToFilter,
            summary_only: true,
            page: pageFilter,
            limit: limitFilter,
        },
        shouldFetchStatementPreview
    );

    const sendStatement = useSendStatementOfAccount({
        action: "email",
        client_ids: getSelectedData
            ? getSelectedData?.map(
                  (item) => item.original.statement_summary.client_id
              )
            : [],
        provider_ids: providersFilter,
        insurance_provider_ids: insuranceProviderIdsFilter,
        custom_date:
            (searchParams.get("custom_date") as IAppCustomDates) || undefined,
        from_date: searchParams.get("from_date"),
        to_date: searchParams.get("to_date"),
        send_to_client_email: true,
    });

    const { data, isLoading } = useFetchClientList(
        {
            page: 1,
            search_string: debouncedValue.toLowerCase(),
            provider_id: practice?.provider_id || "",
            sort_attribute: "client_name",
            sort_order: "ASC",
        },
        (showOptions && searchValue.length > 0) || clientsFilter.length > 0
    );

    const { data: providers } = useFetchProviders(
        {
            search_string: debouncedProviderSearch.toLowerCase(),
            sort_attribute: "provider_name",
            sort_order: "ASC",
        },
        (showProviderOptions && providerSearchValue.length > 0) ||
            providersFilter.length > 0
    );

    const { data: selectedProviders } = useFetchProviders(
        { provider_ids: providersFilter, limit: providersFilter.length },
        providersFilter.length > 0
    );

    const statementDownload = useGetDownloadJobId(
        {
            action: "download",
            client_ids: clientsFilter,
            provider_ids: providersFilter,
            insurance_provider_ids: insuranceProviderIdsFilter,
            custom_date: customDateFilter,
            from_date: fromDateFilter,
            to_date: toDateFilter,
            from_amount_outstanding_coins: allTimeCoInsurance
                ? "0"
                : coInsuranceFromFilter,
            to_amount_outstanding_coins: allTimeCoInsurance
                ? "100000"
                : coInsuranceToFilter,
            from_amount_outstanding_ins: allTimeInsurance
                ? "0"
                : insuranceFromFilter,
            to_amount_outstanding_ins: allTimeInsurance
                ? "100000"
                : insuranceToFilter,
        },
        shouldFetchStatementPreview && isDownloading
    );

    const toast = useToast();

    const exportPdf = useExportPdfs(
        { export_job_id: jobId as string, pdf_type: "statement_of_account" },
        Boolean(jobId) && showLoader
    );

    const ClientNamesForSelect = data?.data.map((client) => ({
        label: `${client.first_name} ${client.last_name}`,
        value: client.client_id,
    }));

    const ProviderNamesForSelect = providers?.data.map((provider) => ({
        label: provider.provider_name,
        value: provider.provider_id,
    }));

    const selectedProviderOptions =
        selectedProviders?.data.map((provider) => ({
            label: provider.provider_name,
            value: provider.provider_id,
        })) || [];

    const providerSelectOptions = [
        ...(ProviderNamesForSelect || []),
        ...selectedProviderOptions.filter(
            (option) =>
                !(ProviderNamesForSelect || []).some(
                    (searched) => searched.value === option.value
                )
        ),
    ];

    const selectedProviderValue = providerSelectOptions.filter((option) =>
        providersFilter.includes(option.value)
    );

    const handleShowEmailDialog = () => {
        setShowEmailDialog((state) => !state);
    };

    const handleClientsChange = (values: unknown) => {
        // Clear array values so we don't have duplicates
        // or values that have been removed from the array

        searchParams.delete("clients[]");
        if ((values as Options[]).length === 0) {
            searchParams.delete("clients[]");
        } else {
            (values as Options[]).forEach((option) => {
                searchParams.append("clients[]", option.value);
            });
        }

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleProviderChange = (values: unknown) => {
        searchParams.delete("providers[]");
        if ((values as Options[]).length > 0) {
            (values as Options[]).forEach((option) => {
                searchParams.append("providers[]", option.value);
            });
        }

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: unknown) => {
        // // Clear value if no value was selected
        if (!value) {
            searchParams.delete("custom_date");
        } else searchParams.set("custom_date", (value as Options).value);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInsuranceProviderIdsChange = (
        insuranceProviderIds: string[]
    ) => {
        setInsuranceProviderIdsFilter(searchParams, insuranceProviderIds);

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCoInsuranceFromChange = (value: string) => {
        if (value === "") searchParams.delete("coInsurance_from");
        else searchParams.set("coInsurance_from", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleCoInsuranceToChange = (value: string) => {
        if (value === "") searchParams.delete("coInsurance_to");
        else searchParams.set("coInsurance_to", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInsuranceFromChange = (value: string) => {
        if (value === "") searchParams.delete("insurance_from");
        else searchParams.set("insurance_from", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleInsuranceToChange = (value: string) => {
        if (value === "") searchParams.delete("insurance_to");
        else searchParams.set("insurance_to", value);
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));

        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handlePageChange = (page: number) => {
        searchParams.set("page", String(page));
        setSearchParams(searchParams);
    };

    const handleLimitChange = (limit: number) => {
        searchParams.set("limit", String(limit));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleRowClick = (row: Row<IStatementOfAccount>) => {
        navigate(
            `/reports/billing-reports/${
                row.original.statement_summary.client_id
            }?${searchParams.toString()}`
        );
    };

    const handleReset = () => {
        searchParams.delete("clients[]");
        searchParams.delete("providers[]");
        searchParams.delete("custom_date");
        setInsuranceProviderIdsFilter(searchParams, []);
        searchParams.delete("from_date");
        searchParams.delete("to_date");
        searchParams.delete("coInsurance_from");
        searchParams.delete("coInsurance_to");
        searchParams.delete("insurance_from");
        searchParams.delete("insurance_to");
        searchParams.set("page", "1");
        setAllTimeCoInsurance(false);
        setAllTimeInsurance(false);
        selectRef.current?.clearValue();
        providerSelectRef.current?.clearValue();
        setSearchParams(searchParams);
    };

    const onSubmit = () => {
        sendStatement.mutate(
            {},
            {
                onSuccess: (res) => {
                    toast.toast({
                        mode: "success",
                        message:
                            res.message ||
                            "Statement of account sent successfully",
                    });
                    handleShowEmailDialog();
                    setRowSelection({});
                },
                onError: (err) => {
                    toast.toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not send statement of account at this time",
                    });
                },
            }
        );
    };

    const handleDownload = async () => {
        setIsDownloading(true);

        if (statementDownload.error) {
            toast.toast({
                mode: "error",
                message:
                    (statementDownload.error.response?.data
                        .message as string) ||
                    "Could not download statement of account.",
            });
        }
        setShowLoader(true);
    };

    React.useEffect(() => {
        setShowDefault(true);
        setShowDefaultProviders(true);
    }, []);

    React.useEffect(() => {
        // Check if the exportPdf query is currently loading
        const isExportPdfLoading = exportPdf.isLoading;

        if (statementDownload.isSuccess) {
            setIsDownloading(false);
        }

        // Check if there's a job ID and the exportPdf query is not loading
        if (jobId && !isExportPdfLoading) {
            setShowLoader(false);
            setJobId(null);
            setRowSelection({});
        }
    }, [statementDownload.isSuccess, jobId, exportPdf.isLoading]);

    React.useEffect(() => {
        if (statementDownload.data?.data.export_job_id) {
            const timeoutId = setTimeout(() => {
                setJobId(statementDownload.data?.data.export_job_id);
            }, 4000);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        return () => {};
    }, [statementDownload.data?.data.export_job_id, statementDownload.isStale]);

    return (
        <>
            <AlertDialog open={showEmailDialog}>
                <AlertDialogContent
                    title="  Are you sure you want to send statement to mail?"
                    description=" By confirming, please note that this action is
                    irreversible"
                    onCancelClick={handleShowEmailDialog}
                    saveText="Yes, confirm"
                    isCancelBtnDisabled={sendStatement.isLoading}
                    isSaveBtnDisabled={sendStatement.isLoading}
                    onSaveClick={onSubmit}
                />
            </AlertDialog>

            <Card classNames="p-0 mb-[--spacing-header]">
                <div>
                    <div className="flex [border-bottom:1px_solid_var(--color-stroke)] p-24 justify-between items-center">
                        <p className="font-medium text-base">Set the details</p>
                        <div>
                            <Button
                                className="ml-3"
                                variant="secondary"
                                type="button"
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </div>
                    </div>
                    <div className="p-24">
                        <form id="statement-preview">
                            <div className="fg">
                                <Select
                                    ref={selectRef}
                                    isSearchable
                                    filterOption={() => true}
                                    isMulti
                                    placeholder="Client name (Type multiple)"
                                    value={
                                        showDefault
                                            ? ClientNamesForSelect?.filter(
                                                  (client) =>
                                                      searchParams
                                                          .getAll("clients[]")
                                                          .includes(
                                                              client.value
                                                          )
                                              ) || []
                                            : undefined
                                    }
                                    aria-label="Client name"
                                    label="Client name"
                                    options={ClientNamesForSelect}
                                    onChange={handleClientsChange}
                                    onInputChange={(value) => {
                                        setShowOptions(true);
                                        setSearchValue(value);
                                        setShowDefault(false);
                                    }}
                                    onBlur={() => {
                                        setShowOptions(false);
                                    }}
                                    defaultValue={
                                        !showDefault
                                            ? ClientNamesForSelect?.filter(
                                                  (client) =>
                                                      searchParams
                                                          .getAll("clients[]")
                                                          .includes(
                                                              client.value
                                                          )
                                              ) || []
                                            : undefined
                                    }
                                    openMenuOnClick={false}
                                    menuIsOpen={searchValue.length > 0}
                                    noOptionsMessage={({ inputValue }) =>
                                        isLoading && inputValue
                                            ? "Fetching clients..."
                                            : "No options"
                                    }
                                />
                            </div>

                            <div className="fg fg-space-between two flex">
                                <div>
                                    <Select
                                        ref={providerSelectRef}
                                        isSearchable
                                        filterOption={() => true}
                                        isMulti
                                        placeholder="Provider name (Type multiple)"
                                        value={
                                            showDefaultProviders
                                                ? selectedProviderValue
                                                : undefined
                                        }
                                        aria-label="Provider name"
                                        label="Provider name"
                                        options={providerSelectOptions}
                                        onChange={handleProviderChange}
                                        onInputChange={(value) => {
                                            setShowProviderOptions(true);
                                            setProviderSearchValue(value);
                                            setShowDefaultProviders(false);
                                        }}
                                        onBlur={() => {
                                            setShowProviderOptions(false);
                                        }}
                                        defaultValue={
                                            !showDefaultProviders
                                                ? selectedProviderValue
                                                : undefined
                                        }
                                        openMenuOnClick={false}
                                        menuIsOpen={
                                            providerSearchValue.length > 0
                                        }
                                    />
                                </div>
                                <div>
                                    <p className="font-medium mb-1 !text-caption text-textSec">
                                        Insurance provider
                                    </p>
                                    <InsuranceProviderFilter
                                        values={insuranceProviderIdsFilter}
                                        onChange={
                                            handleInsuranceProviderIdsChange
                                        }
                                        alwaysShowSelfPay
                                        isSelfPayExclusive
                                        align="start"
                                    />
                                </div>
                            </div>

                            <div className="fg fg-space-between two flex">
                                <div>
                                    <Select
                                        label="Date range"
                                        options={DurationTypesForSelect}
                                        placeholder="Select date range"
                                        onChange={handleCustomDateChange}
                                        value={
                                            DurationTypesForSelect.find(
                                                (option) => {
                                                    if (customDateFilter)
                                                        return (
                                                            option.value ===
                                                            customDateFilter
                                                        );
                                                    return undefined;
                                                }
                                            ) || null
                                        }
                                        isClearable={Boolean(customDateFilter)}
                                    />
                                </div>
                                <div>
                                    <p className="font-medium mb-1 !text-caption text-textSec">
                                        Date
                                    </p>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <div className="flex justify-between h-8 [border:0.5px_solid_var(--color-stroke)] shadow-active px-2 rounded-md border-[1.5px] border-[#E7E7E7] rounded-r8 items-center bg-[--input-bg]">
                                                <Button
                                                    className="w-full text-xs justify-start font-normal text-textSec data-[state=open]:border-primary shadow-active"
                                                    variant="normal"
                                                    mode="outline"
                                                    aria-label="request-changes"
                                                    type="button"
                                                >
                                                    {fromDateFilterToDate &&
                                                    toDateFilterToDate
                                                        ? `${format(
                                                              fromDateFilterToDate,
                                                              "MM/dd/yyyy"
                                                          )} - ${format(
                                                              toDateFilterToDate,
                                                              "MM/dd/yyyy"
                                                          )}`
                                                        : "Select date"}
                                                </Button>
                                                <div className="pr-8">
                                                    <CalendarIcon className="size-4" />
                                                </div>
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent align="start">
                                            <DatePicker
                                                label="From"
                                                placeholderText="MM/DD/YYYY"
                                                selected={fromDateFilterToDate}
                                                onChange={
                                                    handleFromDateFilterChange
                                                }
                                                maxDate={
                                                    toDateFilterToDate ||
                                                    new Date(Date.now())
                                                }
                                                isClearable
                                            />

                                            <DatePicker
                                                label="To"
                                                placeholderText="MM/DD/YYYY"
                                                selected={toDateFilterToDate}
                                                onChange={
                                                    handleToDateFilterChange
                                                }
                                                minDate={fromDateFilterToDate}
                                                isClearable
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                            <div className="fg fg-space-between two flex">
                                <div>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <div>
                                                <p className="text-xs font-medium pb-4">
                                                    Co-insurance Due
                                                </p>
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    className="w-full justify-between h-8 px-16 shadow-active gap-x-8 text-xs text-textSec data-[state=open]:border-primary [border:0.5px_solid_var(--color-stroke)] font-normal self-start md:self-center bg-[--input-bg]"
                                                >
                                                    Select range
                                                    <ChevronDownIcon
                                                        stroke={
                                                            APP_COLORS.COLOR_BLACK
                                                        }
                                                    />
                                                </Button>
                                                <div className="pt-2">
                                                    <FormCheckbox
                                                        label="All time co-insurance due"
                                                        value="All time co-insurance due"
                                                        labelClass="text-sm font-normal"
                                                        checked={
                                                            allTimeCoInsurance
                                                        }
                                                        onChange={(e) => {
                                                            setAllTimeCoInsurance(
                                                                e.currentTarget
                                                                    .checked
                                                            );
                                                            searchParams.set(
                                                                "page",
                                                                "1"
                                                            );
                                                            setSearchParams(
                                                                searchParams
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            align="start"
                                            className="h-auto w-[396px] px-16 pt-6 z-20"
                                        >
                                            <div className="flex flex-col gap-y-[14px]">
                                                <div>
                                                    <p className="pb-1 text-textSec">
                                                        From
                                                    </p>
                                                    <Dropdown>
                                                        <DropdownTrigger
                                                            asChild
                                                        >
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className={cn(
                                                                    "px-2 h-7 w-full justify-between shadow-active gap-x-8 text-sm data-[state=open]:border-primary font-medium capitalize"
                                                                )}
                                                            >
                                                                {amountDue && (
                                                                    <>
                                                                        {truncateString(
                                                                            showMoneyInAppFormat(
                                                                                coInsuranceFromFilter as string
                                                                            )
                                                                        )}
                                                                        <ChevronDownIcon
                                                                            stroke={
                                                                                APP_COLORS.COLOR_BLACK
                                                                            }
                                                                        />
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </DropdownTrigger>
                                                        <DropdownContent
                                                            align="start"
                                                            width="auto"
                                                            maxHeight={216}
                                                            className="overflow-y-auto"
                                                        >
                                                            {amountDue.map(
                                                                (amount) => (
                                                                    <DropdownItem
                                                                        isSelected={
                                                                            coInsuranceFromFilter ===
                                                                            amount
                                                                        }
                                                                        key={
                                                                            amount
                                                                        }
                                                                        onClick={() =>
                                                                            handleCoInsuranceFromChange(
                                                                                amount
                                                                            )
                                                                        }
                                                                    >
                                                                        {showMoneyInAppFormat(
                                                                            amount
                                                                        )}
                                                                    </DropdownItem>
                                                                )
                                                            )}
                                                        </DropdownContent>
                                                    </Dropdown>
                                                </div>
                                                <div>
                                                    <p className="pb-4 text-textSec">
                                                        To
                                                    </p>
                                                    <Dropdown>
                                                        <DropdownTrigger
                                                            asChild
                                                        >
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className={cn(
                                                                    "px-16 w-full h-8 justify-between text-textSec  shadow-active gap-x-8 text-sm data-[state=open]:border-primary [border:1px_solid_var(--color-stroke)] font-medium capitalize bg-[--input-bg]"
                                                                )}
                                                            >
                                                                {amountDue && (
                                                                    <>
                                                                        {truncateString(
                                                                            showMoneyInAppFormat(
                                                                                coInsuranceToFilter as string
                                                                            )
                                                                        )}
                                                                        <ChevronDownIcon
                                                                            stroke={
                                                                                APP_COLORS.COLOR_BLACK
                                                                            }
                                                                        />
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </DropdownTrigger>
                                                        <DropdownContent
                                                            align="start"
                                                            width="auto"
                                                            maxHeight={216}
                                                            className="overflow-y-auto"
                                                        >
                                                            {amountDue.map(
                                                                (amount) => (
                                                                    <DropdownItem
                                                                        className={cn(
                                                                            "flex gap-x-8 items-center uppercase text-xs font-medium",
                                                                            {
                                                                                "text-primary":
                                                                                    coInsuranceToFilter ===
                                                                                    amount,
                                                                            }
                                                                        )}
                                                                        key={
                                                                            amount
                                                                        }
                                                                        onClick={() =>
                                                                            handleCoInsuranceToChange(
                                                                                amount
                                                                            )
                                                                        }
                                                                    >
                                                                        {showMoneyInAppFormat(
                                                                            amount
                                                                        )}
                                                                        {coInsuranceToFilter ===
                                                                        amount ? (
                                                                            <CheckPrimaryColorIcon />
                                                                        ) : null}
                                                                    </DropdownItem>
                                                                )
                                                            )}
                                                        </DropdownContent>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div>
                                    <Popover>
                                        <PopoverTrigger
                                            asChild
                                            className="w-full"
                                        >
                                            <div>
                                                <p className="text-xs font-medium pb-4">
                                                    Insurance Due
                                                </p>
                                                <Button
                                                    size="auto"
                                                    variant="normal"
                                                    className="w-full justify-between px-2 h-8 shadow-active gap-x-8 text-xs text-gray data-[state=open]:border-primary [border:1px_solid_var(--color-stroke)] font-normal self-start md:self-center"
                                                >
                                                    Select range
                                                    <ChevronDownIcon
                                                        stroke={
                                                            APP_COLORS.COLOR_BLACK
                                                        }
                                                    />
                                                </Button>
                                                <div className="pt-8">
                                                    <FormCheckbox
                                                        label="All time insurance due"
                                                        value="All time insurance due"
                                                        checked={
                                                            allTimeInsurance
                                                        }
                                                        labelClass="text-sm font-normal"
                                                        onChange={(e) => {
                                                            setAllTimeInsurance(
                                                                e.currentTarget
                                                                    .checked
                                                            );
                                                            searchParams.set(
                                                                "page",
                                                                "1"
                                                            );
                                                            setSearchParams(
                                                                searchParams
                                                            );
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            align="start"
                                            className="h-auto w-[396px] px-16 pt-6 z-20"
                                        >
                                            <div className="flex flex-col gap-y-[14px] ">
                                                <div>
                                                    <p className="pb-4 text-gray">
                                                        From
                                                    </p>
                                                    <Dropdown>
                                                        <DropdownTrigger
                                                            asChild
                                                        >
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className={cn(
                                                                    "px-16 w-full justify-between h-8 shadow-active gap-x-8 text-sm data-[state=open]:border-primary font-medium capitalize"
                                                                )}
                                                            >
                                                                {amountDue && (
                                                                    <>
                                                                        {truncateString(
                                                                            showMoneyInAppFormat(
                                                                                insuranceFromFilter as string
                                                                            )
                                                                        )}
                                                                        <ChevronDownIcon
                                                                            stroke={
                                                                                APP_COLORS.COLOR_BLACK
                                                                            }
                                                                        />
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </DropdownTrigger>
                                                        <DropdownContent
                                                            align="start"
                                                            width="auto"
                                                            maxHeight={216}
                                                            className="overflow-y-auto"
                                                        >
                                                            {amountDue.map(
                                                                (amount) => (
                                                                    <DropdownItem
                                                                        isSelected={
                                                                            insuranceFromFilter ===
                                                                            amount
                                                                        }
                                                                        key={
                                                                            amount
                                                                        }
                                                                        onClick={() =>
                                                                            handleInsuranceFromChange(
                                                                                amount
                                                                            )
                                                                        }
                                                                    >
                                                                        {showMoneyInAppFormat(
                                                                            amount
                                                                        )}
                                                                        {insuranceFromFilter ===
                                                                        amount ? (
                                                                            <CheckPrimaryColorIcon />
                                                                        ) : null}
                                                                    </DropdownItem>
                                                                )
                                                            )}
                                                        </DropdownContent>
                                                    </Dropdown>
                                                </div>
                                                <div>
                                                    <p className="pb-4 text-gray">
                                                        To
                                                    </p>
                                                    <Dropdown>
                                                        <DropdownTrigger
                                                            asChild
                                                        >
                                                            <Button
                                                                size="auto"
                                                                variant="normal"
                                                                className={cn(
                                                                    "px-16 h-8 w-full justify-between shadow-active gap-x-8 text-sm data-[state=open]:border-primary font-medium capitalize"
                                                                )}
                                                            >
                                                                {amountDue && (
                                                                    <>
                                                                        {truncateString(
                                                                            showMoneyInAppFormat(
                                                                                insuranceToFilter as string
                                                                            )
                                                                        )}
                                                                        <ChevronDownIcon
                                                                            stroke={
                                                                                APP_COLORS.COLOR_BLACK
                                                                            }
                                                                        />
                                                                    </>
                                                                )}
                                                            </Button>
                                                        </DropdownTrigger>
                                                        <DropdownContent
                                                            align="start"
                                                            width="auto"
                                                            maxHeight={216}
                                                            className="overflow-y-auto"
                                                        >
                                                            {amountDue.map(
                                                                (amount) => (
                                                                    <DropdownItem
                                                                        className={cn(
                                                                            "flex gap-x-8 items-center uppercase text-xs font-medium",
                                                                            {
                                                                                "text-primary":
                                                                                    insuranceToFilter ===
                                                                                    amount,
                                                                            }
                                                                        )}
                                                                        key={
                                                                            amount
                                                                        }
                                                                        onClick={() =>
                                                                            handleInsuranceToChange(
                                                                                amount
                                                                            )
                                                                        }
                                                                    >
                                                                        {showMoneyInAppFormat(
                                                                            amount
                                                                        )}
                                                                        {insuranceToFilter ===
                                                                        amount ? (
                                                                            <CheckPrimaryColorIcon />
                                                                        ) : null}
                                                                    </DropdownItem>
                                                                )
                                                            )}
                                                        </DropdownContent>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </Card>

            {statementPreview.isLoading && shouldFetchStatementPreview && (
                <Loader />
            )}

            {statementPreview.error &&
                statementPreview.error.response?.status !== 404 && (
                    <ListState
                        isError
                        stateHelperText="Try reloading this page to solve this issue"
                        errorMsg={
                            statementPreview.error?.response?.data.message ||
                            `Cannot display preview at this time please try again later`
                        }
                    />
                )}

            {statementPreview.isSuccess &&
                Object.values(statementPreview.data?.data[0]?.client_statements)
                    .length === 0 && (
                    <ListState
                        stateHelperText="Statement of account for the applied filters will appear here once they are added"
                        isError={false}
                        emptyMessage={statementPreview.data.message}
                    />
                )}

            {statementPreview.isSuccess &&
                Object.values(statementPreview.data?.data[0]?.client_statements)
                    .length > 0 && (
                    <>
                        <Dialog open={showLoader}>
                            <DialogContent
                                title="Download in progress"
                                variant="center"
                                handleCloseDialog={() => setShowLoader(false)}
                                showFooter={false}
                            >
                                <ExportLoaderAndText />
                            </DialogContent>
                        </Dialog>
                        {getTotalAmountOfTableRowsSelected(rowSelection)
                            .amount > 0 && (
                            <div className="flex items-center mb-[--spacing-items] gap-[--spacing-items] flex-wrap]">
                                <div className="font-medium text-base shrink-0">
                                    {
                                        getTotalAmountOfTableRowsSelected(
                                            rowSelection
                                        ).amountString
                                    }
                                    :
                                </div>
                                <Button
                                    variant="secondary"
                                    mode="outline"
                                    type="button"
                                    onClick={handleDownload}
                                >
                                    Download statement
                                </Button>
                                <Button
                                    variant="secondary"
                                    mode="outline"
                                    type="button"
                                    onClick={handleShowEmailDialog}
                                >
                                    Send to mail
                                </Button>
                            </div>
                        )}

                        <Table
                            columns={columns}
                            data={Object.values(
                                statementPreview.data?.data[0].client_statements
                            )}
                            handleRowClick={handleRowClick}
                            setRowSelection={setRowSelection}
                            rowSelection={rowSelection}
                            setGetSelectedData={setGetSelectedData}
                            hasPagination={
                                statementPreview.data.total_count > limitFilter
                            }
                            pagination={
                                <Pagination
                                    totalCount={
                                        statementPreview.data.total_count
                                    }
                                    totalPages={
                                        statementPreview.data.total_pages
                                    }
                                    currentPage={pageFilter}
                                    onPageChange={handlePageChange}
                                    limit={limitFilter}
                                    onLimitChange={handleLimitChange}
                                />
                            }
                        />
                    </>
                )}
        </>
    );
}
